import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import * as service from '../services';
import { FilterNames, PrecificacaoFilterListParams, SelectObjType, FilterObjType } from '../types';

const filterObj: FilterObjType = {
  list: [],
  value: [],
  cache: [],
};

export const useGetPrecificacaoFilterList = () => {
  const [timer, setTimer] = useState(null);
  const [familiaList, setFamiliaList] = useState(filterObj);
  const [tierList, setTierList] = useState(filterObj);
  const [descricaoList, setDescricaoList] = useState(filterObj);

  const nameTypes = {
    familias: familiaList,
    tiers: tierList,
    descricoes: descricaoList,
  };

  const set = {
    familias: setFamiliaList,
    tiers: setTierList,
    descricoes: setDescricaoList,
  };

  const setList = (name: FilterNames, list: string[]) => {
    const newList = list.map(item => ({
      label: item,
      value: item,
    }));

    const cacheList = nameTypes[name].cache;
    const removeDuplicate = newList?.filter(i => !nameTypes[name].value.includes(i.value));

    const concatList = nameTypes[name].value.length ? cacheList.concat(removeDuplicate) : newList;

    set[name]({
      ...nameTypes[name],
      list: concatList,
    });
  };

  const handleSearchPrecificacaoFilters = (name: FilterNames, query: string) => {
    const payload = {
      page: 0,
      size: 50,
      query: query || '',
    };
    const set = {
      familias: getFamiliasFilterList,
      tiers: getTierFilterList,
      descricoes: getDescricaoFilterList,
    };
    clearTimeout(timer);

    const newTimer = setTimeout(() => {
      set[name].mutateAsync(payload);
    }, 500);

    setTimer(newTimer);
  };

  const handleAddFilterItem = (name: FilterNames, item: SelectObjType) => {
    const valueList = nameTypes[name].value;
    const newValue = valueList.concat(item.value);
    const filterItem = {
      ...nameTypes[name],
      value: newValue,
      cache: [...nameTypes[name].cache, item],
    };

    set[name](filterItem);
  };

  const handleRemoveFilterItem = (name: FilterNames, item: SelectObjType) => {
    const removedCache = nameTypes[name].cache.filter(i => i.value !== item.value);
    const newValue = nameTypes[name].value.filter(i => i !== item.value);

    const filterItem = {
      ...nameTypes[name],
      value: newValue,
      cache: removedCache,
    };

    set[name](filterItem);
  };

  const handleSelectFilterItem = (name: FilterNames, item: SelectObjType) => {
    if (nameTypes[name]?.value.includes(item.value)) {
      handleRemoveFilterItem(name, item);
    } else {
      handleAddFilterItem(name, item);
    }
  };

  const handleCleanFilter = (name: FilterNames) => {
    const obj = {
      ...nameTypes[name],
      value: [],
      cache: [],
    };
    set[name](obj);
  };

  const querySuccess = (name: FilterNames, data: AxiosResponse<any>) => {
    const list = data.data.content;
    setList(name, list);
  };

  const getFamiliasFilterList = useMutation({
    mutationFn: (payload: PrecificacaoFilterListParams) => service.getPlataformaPrecificacaoFilters('familias', payload),
    onSuccess: payload => querySuccess('familias', payload),
  });

  const getTierFilterList = useMutation({
    mutationFn: (payload: PrecificacaoFilterListParams) => service.getPlataformaPrecificacaoFilters('tiers', payload),
    onSuccess: payload => querySuccess('tiers', payload),
  });

  const getDescricaoFilterList = useMutation({
    mutationFn: (payload: PrecificacaoFilterListParams) => service.getPlataformaPrecificacaoFilters('descricoes', payload),
    onSuccess: payload => querySuccess('descricoes', payload),
  });

  return {
    familiaList,
    tierList,
    descricaoList,
    handleSearchPrecificacaoFilters,
    handleSelectFilterItem,
    handleCleanFilter,
  };
};
